// ===========================================
// Time Slot Heatmap Component
// ===========================================
// Visual heatmap showing booking popularity by day and time
// Phase 2 Priority 12: Reporting & Analytics - Week 4
// CRITICAL SHOWPIECE COMPONENT for hybrid booking system

import React from 'react';
import { Paper, Typography, Box, Tooltip, Skeleton } from '@mui/material';
import { TimeSlotData } from '../../api/reports.api';

interface TimeSlotHeatmapProps {
  data?: TimeSlotData[];
  isLoading: boolean;
}

const DAYS = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];
const TIME_SLOTS = [
  '09:00',
  '09:30',
  '10:00',
  '10:30',
  '11:00',
  '11:30',
  '12:00',
  '12:30',
  '13:00',
  '13:30',
  '14:00',
  '14:30',
  '15:00',
  '15:30',
  '16:00',
  '16:30',
  '17:00',
  '17:30',
  '18:00',
  '18:30',
];

const getHeatmapColor = (utilizationRate: number): string => {
  if (utilizationRate === 0) return '#f5f5f5'; // Light gray
  if (utilizationRate < 25) return '#e3f2fd'; // Very light blue
  if (utilizationRate < 50) return '#90caf9'; // Light blue
  if (utilizationRate < 75) return '#64b5f6'; // Medium blue
  if (utilizationRate < 90) return '#42a5f5'; // Blue
  return '#4580E4'; // Primary brand blue (high utilization)
};

const TimeSlotHeatmap: React.FC<TimeSlotHeatmapProps> = ({ data, isLoading }) => {
  // `Array.isArray`, not a truthiness check: the API layer once handed this the
  // WRAPPER object ({ timeSlots, days, ... }) instead of the array, and `!data`
  // happily passed an object straight into `.find` — crashing the whole page,
  // which has no error boundary above it. A wrong shape must degrade to empty.
  const slots: TimeSlotData[] = Array.isArray(data) ? data : [];

  // Transform data into grid structure
  const getUtilizationRate = (day: string, timeSlot: string): number => {
    const slot = slots.find((s) => s.dayOfWeek === day && s.timeSlot === timeSlot);
    return slot?.utilizationRate ?? 0;
  };

  const getBookingCount = (day: string, timeSlot: string): number => {
    const slot = slots.find((s) => s.dayOfWeek === day && s.timeSlot === timeSlot);
    return slot?.bookingCount ?? 0;
  };

  if (isLoading) {
    return (
      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" gutterBottom>
          Time Slot Popularity Heatmap
        </Typography>
        <Skeleton variant="rectangular" height={600} />
      </Paper>
    );
  }

  return (
    <Paper sx={{ p: 3, mb: 3 }}>
      <Typography variant="h6" gutterBottom>
        Time Slot Popularity Heatmap
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Visual representation of booking patterns across days and times. Darker colors indicate
        higher utilization.
      </Typography>

      {/* Legend */}
      <Box sx={{ display: 'flex', gap: 2, mb: 3, alignItems: 'center', flexWrap: 'wrap' }}>
        <Typography variant="caption" color="text.secondary">
          Utilization:
        </Typography>
        {[
          { label: '0%', color: '#f5f5f5' },
          { label: '1-25%', color: '#e3f2fd' },
          { label: '25-50%', color: '#90caf9' },
          { label: '50-75%', color: '#64b5f6' },
          { label: '75-90%', color: '#42a5f5' },
          { label: '90-100%', color: '#4580E4' },
        ].map((item) => (
          <Box key={item.label} sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <Box
              sx={{
                width: 20,
                height: 20,
                bgcolor: item.color,
                border: '1px solid #ddd',
                borderRadius: 0.5,
              }}
            />
            <Typography variant="caption">{item.label}</Typography>
          </Box>
        ))}
      </Box>

      {/* Heatmap Grid */}
      <Box
        sx={{
          overflowX: 'auto',
          overflowY: 'auto',
          maxHeight: 600,
        }}
      >
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: `100px repeat(${DAYS.length}, 80px)`,
            gap: 0.5,
            minWidth: 'fit-content',
          }}
        >
          {/* Header Row */}
          <Box /> {/* Empty top-left corner */}
          {DAYS.map((day) => (
            <Box
              key={day}
              sx={{
                p: 1,
                textAlign: 'center',
                fontWeight: 'bold',
                fontSize: '0.875rem',
              }}
            >
              {day.slice(0, 3)}
            </Box>
          ))}

          {/* Time Slot Rows */}
          {TIME_SLOTS.map((timeSlot) => (
            <React.Fragment key={timeSlot}>
              {/* Time Label */}
              <Box
                sx={{
                  p: 1,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'flex-end',
                  fontSize: '0.75rem',
                  color: 'text.secondary',
                }}
              >
                {timeSlot}
              </Box>

              {/* Day Cells */}
              {DAYS.map((day) => {
                const utilizationRate = getUtilizationRate(day, timeSlot);
                const bookingCount = getBookingCount(day, timeSlot);
                const color = getHeatmapColor(utilizationRate);

                return (
                  <Tooltip
                    key={`${day}-${timeSlot}`}
                    title={
                      <Box>
                        <Typography variant="body2">
                          {day} {timeSlot}
                        </Typography>
                        <Typography variant="caption">
                          {bookingCount} booking{bookingCount !== 1 ? 's' : ''} (
                          {utilizationRate.toFixed(1)}% utilization)
                        </Typography>
                      </Box>
                    }
                    arrow
                  >
                    <Box
                      sx={{
                        bgcolor: color,
                        border: '1px solid #ddd',
                        borderRadius: 0.5,
                        minHeight: 40,
                        minWidth: 80,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        cursor: 'pointer',
                        transition: 'transform 0.2s, box-shadow 0.2s',
                        '&:hover': {
                          transform: 'scale(1.05)',
                          boxShadow: 2,
                          zIndex: 1,
                        },
                      }}
                    >
                      {bookingCount > 0 && (
                        <Typography
                          variant="caption"
                          sx={{
                            fontWeight: 'bold',
                            color: utilizationRate > 50 ? 'white' : 'text.primary',
                          }}
                        >
                          {bookingCount}
                        </Typography>
                      )}
                    </Box>
                  </Tooltip>
                );
              })}
            </React.Fragment>
          ))}
        </Box>
      </Box>
    </Paper>
  );
};

export default TimeSlotHeatmap;
